import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useEffect, isValidElement, cloneElement, useMemo } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useFocus, useInteractions, useClick, useDismiss, useHover, flip, autoUpdate } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { Button } from '../../Controls/Button';
import Icons from '../../Elements/Icons';
import { DropdownItem } from './components';
import { ANIMATION_DURATION_MS, DEFAULT_Z_INDEX } from './constants';
import { DropdownContextProvider, useDropdownContext } from './context';
import { getPlacement, stopPropagation } from './helpers';
import { ButtonWrapper, InnerWrapper, Wrapper } from './styled';
const DropdownListComponent = memo(function DropdownListComponent({ children, id, className, disabled, buttonComponent, renderButton, align = 'left', zIndex = DEFAULT_Z_INDEX, minWidth = true, onVisibilityChange, isSubmenu: isSubmenuProp = false, dataTestId, }) {
    const isChildrenArrayEmpty = Array.isArray(children) ? children.every((c) => !!c === false) : false;
    const { isShown, update } = useDropdownContext();
    const autoDetectedSubmenu = isValidElement(buttonComponent) && buttonComponent.type === DropdownItem;
    const isSubmenu = isSubmenuProp || autoDetectedSubmenu;
    const renderedButton = useMemo(() => renderButton && renderButton(isShown), [isShown, renderButton]);
    const { refs, floatingStyles, context, placement } = useFloating({
        open: isShown,
        placement: getPlacement(align, isSubmenu),
        onOpenChange: (open) => {
            update(open);
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(1),
            flip({
                fallbackPlacements: isSubmenu
                    ? ['left', 'right-start', 'left-start', 'right-end', 'left-end']
                    : ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
            }),
        ],
    });
    const isTop = placement.startsWith('top');
    const focus = useFocus(context);
    const click = useClick(context, {
        enabled: !isSubmenu,
    });
    const hover = useHover(context, {
        enabled: isSubmenu,
        delay: { open: 150, close: 100 },
    });
    const dismiss = useDismiss(context, {
        enabled: isShown,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([focus, click, hover, dismiss]);
    useEffect(() => {
        onVisibilityChange?.(isShown);
    }, [isShown, onVisibilityChange]);
    if (isChildrenArrayEmpty) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [createPortal(_jsx(CSSTransition, { in: isShown, timeout: ANIMATION_DURATION_MS, transitionAppear: true, unmountOnExit: true, children: _jsx(Wrapper, { ...getFloatingProps(), style: floatingStyles, zIndex: zIndex, isTop: isTop, visible: isShown, ref: refs.setFloating, "data-testid": 'dropdown-wrapper', children: _jsx(InnerWrapper, { minWidth: minWidth, zIndex: zIndex, "data-testid": 'dropdown-inner-wrapper', children: children }) }) }), document.body), _jsxs(ButtonWrapper, { onClick: stopPropagation, children: [renderButton &&
                        isValidElement(renderedButton) &&
                        cloneElement(renderedButton, {
                            ...getReferenceProps(),
                            ref: refs.setReference,
                            // Only override dataTestId if the rendered button doesn't already have one
                            ...(renderedButton.props.dataTestId ? {} : { dataTestId }),
                        }), buttonComponent &&
                        isValidElement(buttonComponent) &&
                        cloneElement(buttonComponent, {
                            ...getReferenceProps(),
                            ref: refs.setReference,
                            dataTestId,
                        }), !buttonComponent && !renderButton && (_jsx(Button, { ...getReferenceProps(), id: id, className: className, disabled: disabled, primary: true, iconOnly: true, ref: refs.setReference, dataTestId: dataTestId, children: _jsx(Icons.Chevron, { align: isShown ? 'top' : 'bottom' }) }))] })] }));
});
const DropdownListWrapper = memo(function DropdownListWrapper(props) {
    return (_jsx(DropdownContextProvider, { children: _jsx(DropdownListComponent, { ...props }) }));
});
export const DropdownList = Object.assign(DropdownListWrapper, {
    DropdownItem,
});
export { renderMoreButton } from './helpers';
